import { Box, Text } from "@chakra-ui/react";
import { format as formatDate, isValid, parse, startOfDay } from "date-fns";
import { type KeyboardEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { DatePickerGlobalStyles } from "./DatePickerGlobalStyles";
import { DatePickerHeader } from "./DatePickerHeader";
import {
  PORTAL_ID,
  PickerCustomInput,
  datepickerLocaleMap,
  parsePickerIsoDate,
  useDatepickerPortal,
  usePickerLanguage,
  SelectedDateAnnouncement,
} from "./pickerInternals";

interface ThemedDatePickerProps {
  id?: string;
  ariaLabel?: string;
  defaultDate?: string;
  date?: string | null;
  onChange: (date?: string) => Promise<void> | void;
  onBlur?: () => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  placeholder?: string;
  allowPastDates?: boolean;
  minDate?: Date;
  isClearable?: boolean;
}

export const DATE_FIELD_FORMAT = "dd.MM.yyyy";

const isReadableDate = (text: string) => {
  const parsed = parse(text, DATE_FIELD_FORMAT, new Date());
  return isValid(parsed) && formatDate(parsed, DATE_FIELD_FORMAT) === text;
};

export const ThemedDatePicker = ({
  id,
  ariaLabel,
  defaultDate,
  date,
  onChange,
  onBlur,
  isDisabled,
  autoFocus = false,
  placeholder,
  allowPastDates,
  minDate,
  isClearable = false,
}: ThemedDatePickerProps) => {
  const { t, i18n } = useTranslation();
  const intlLocale = datepickerLocaleMap[i18n.language] ?? "nb-NO";
  const [internalDate, setInternalDate] = useState<Date | null>(parsePickerIsoDate(defaultDate));
  const selected = date !== undefined ? parsePickerIsoDate(date) : internalDate;

  const pickerLanguage = usePickerLanguage();
  useDatepickerPortal();

  const earliest = allowPastDates ? null : startOfDay(minDate ?? new Date());
  const [refusedTyping, setRefusedTyping] = useState(false);
  const [unreadableTyping, setUnreadableTyping] = useState(false);
  const typedText = useRef("");

  const handleChange = (d: Date | null) => {
    setRefusedTyping(false);
    setUnreadableTyping(false);
    typedText.current = "";
    setInternalDate(d);
    onChange(d ? formatDate(d, "yyyy-MM-dd") : undefined);
  };

  const refuseTypedDateBeforeEarliest = (event?: { target: EventTarget | null }) => {
    if (!earliest || !(event?.target instanceof HTMLInputElement)) return;
    const typed = parse(event.target.value, DATE_FIELD_FORMAT, new Date());
    setRefusedTyping(isValid(typed) && event.target.value.length === DATE_FIELD_FORMAT.length && typed < earliest);
  };

  const checkTypedText = (event?: { target: EventTarget | null }) => {
    if (event?.target instanceof HTMLInputElement) {
      typedText.current = event.target.value.trim();
      if (typedText.current === "" || isReadableDate(typedText.current)) setUnreadableTyping(false);
    }
    refuseTypedDateBeforeEarliest(event);
  };

  const flagUnreadableTyping = () => {
    setUnreadableTyping(typedText.current !== "" && !isReadableDate(typedText.current));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === "Enter") flagUnreadableTyping();
    clearOnDeleteKey(event);
  };

  const clearOnDeleteKey = (event: KeyboardEvent<HTMLElement>) => {
    if (!isClearable || !selected || (event.key !== "Backspace" && event.key !== "Delete")) return;
    event.preventDefault();
    handleChange(null);
  };

  return (
    <Box sx={{ ".react-datepicker-wrapper": { maxWidth: { sm: "full", md: "60" } } }} onBlur={flagUnreadableTyping}>
      <DatePickerGlobalStyles />
      <DatePicker
        id={id}
        selected={selected}
        onChange={handleChange}
        onCalendarClose={onBlur}
        autoFocus={autoFocus}
        dateFormat={DATE_FIELD_FORMAT}
        strictParsing
        placeholderText={placeholder ?? "dd.mm.yyyy"}
        customInput={
          <PickerCustomInput
            aria-label={ariaLabel}
            onClear={isClearable ? () => handleChange(null) : undefined}
            clearLabel={t("Clear date") ?? undefined}
            isTypeable
          />
        }
        disabled={isDisabled}
        onKeyDown={handleKeyDown}
        onChangeRaw={checkTypedText}
        minDate={allowPastDates ? undefined : (minDate ?? new Date())}
        showPopperArrow={false}
        popperPlacement="bottom-start"
        portalId={PORTAL_ID}
        renderCustomHeader={(props) => <DatePickerHeader {...props} locale={intlLocale} />}
        {...pickerLanguage}
      />
      {refusedTyping && earliest && (
        <Text fontSize="xs" color="smError" mt="1" role="alert">
          {t("Choose a date on or after {{date}}.", { date: formatDate(earliest, DATE_FIELD_FORMAT) })}
        </Text>
      )}
      {unreadableTyping && (
        <Text fontSize="xs" color="smError" mt="1" role="alert">
          {t("Write the date as dd.mm.yyyy.")}
        </Text>
      )}
      <SelectedDateAnnouncement selected={selected} withTime={false} locale={intlLocale} />
    </Box>
  );
};
